import { c as _c } from "react/compiler-runtime";
import React from "react";
import type { Post, Media } from "@/payload-types";
import { getMediaUrl, type MediaSize } from "@/utilities/media/getMediaUrl";
import { VideoPlayIndicator } from "@/components/ui/VideoPlayIndicator";
import { FillImage } from "@/components/ui/FillImage";
const DEFAULT_POST_IMAGE = "/assets/img/default-post-image.jpg";

/**
 * Extract hero image URL from a Post, with fallback to default placeholder.
 * Use this when passing a URL string to external components (e.g. ContentCard).
 *
 * @param size - Pre-generated Payload image size to use (defaults to original)
 */
export function getPostImageUrl(post: Post, size?: MediaSize): string {
  const heroImage = post.heroImage as Media | null;
  return getMediaUrl(heroImage, size) ?? DEFAULT_POST_IMAGE;
}
type AspectRatio = "video" | "9/16" | "square";
const ASPECT_CLASSES: Record<AspectRatio, string> = {
  video: "aspect-video",
  "9/16": "aspect-[9/16]",
  square: "aspect-square"
};
const SIZES_MAP: Record<AspectRatio, string> = {
  video: "(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 400px",
  "9/16": "(max-width: 768px) 100vw, 300px",
  square: "(max-width: 768px) 100vw, 300px"
};
export interface PostImageProps {
  src?: string | null;
  alt?: string;
  aspectRatio?: AspectRatio;
  className?: string;
  /** Overlay a play indicator to mark the post as a video. */
  isVideo?: boolean;
}

/**
 * Renders a post image with cover fit, correct aspect ratio, and fallback to a default placeholder.
 * The image itself is a `FillImage`: plain unoptimized `src` by default, or a `srcset` of Payload
 * renditions when rendered inside a `MediaVariantsProvider` that knows the url.
 */
export function PostImage(t0) {
  const $ = _c(10);
  const {
    src,
    alt: t1,
    aspectRatio: t2,
    className: t3,
    isVideo: t4
  } = t0;
  const alt = t1 === undefined ? "" : t1;
  const aspectRatio = t2 === undefined ? "video" : t2;
  const className = t3 === undefined ? "" : t3;
  const isVideo = t4 === undefined ? false : t4;
  const imageUrl = src || DEFAULT_POST_IMAGE;
  const t5 = `relative overflow-hidden bg-muted ${ASPECT_CLASSES[aspectRatio]} ${className}`;
  const t6 = SIZES_MAP[aspectRatio];
  let t7;
  if ($[0] !== alt || $[1] !== imageUrl || $[2] !== t6) {
    t7 = <FillImage src={imageUrl} alt={alt} sizes={t6} className="object-cover" />;
    $[0] = alt;
    $[1] = imageUrl;
    $[2] = t6;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  let t8;
  if ($[4] !== isVideo) {
    t8 = isVideo && <VideoPlayIndicator />;
    $[4] = isVideo;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  let t9;
  if ($[6] !== t5 || $[7] !== t7 || $[8] !== t8) {
    t9 = <div className={t5}>{t7}{t8}</div>;
    $[6] = t5;
    $[7] = t7;
    $[8] = t8;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  return t9;
}